/**
 * 群成员操作
 * @author sunzhiguang
 * @date 2020/7/14
 */

import React, { useCallback, useMemo } from "react";
import "./index.less";
import { GroupAttribute, GroupRoster, SessionType, UID } from "@/types/chat";
import { HeadShowType } from "@/types/chat/enum";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import ImService from "@/server/ImService";
import HeadPortrait from "@/baseComponents/Chat/HeadPortrait";
import Prompt from "@/baseComponents/ModalComponent/prompt";
import { UserCardPayload } from "@/models";
import {
  employeeToUid,
  getUnitTitleInfo,
  isCardViewGroupSession,
  isEqualEmployee
} from "@/utils/chat/message";
import {
  checkShowDropdownOperate,
  isGroupAdmin,
  isGroupOrdinary,
  isGroupOwner
} from "@/utils/chat/group";
import IconFont from "@/components/icon";
import Context from "@/components/chat/context";
import { inviteUser } from "../GroupUserSelector/common";
import { removeStrangerToGroupRecord } from "@/api/chat";
import bus from "@/utils/bus";

interface MemberOperateProps {
  member: any;
  keyword: string;
  canAddMember?: boolean;
}
interface DvaDispatchProps {
  removeGroupMember: Function;
  toggleUserCardModal: (payload: UserCardPayload) => void;
}

type IProps = Readonly<
  MemberOperateProps & DvaDispatchProps & ChatState & Partial<{ getAllGroupMembers?: Function }>
>;

function GroupMemberOperate(props: IProps) {
  const {
    selectedSession,
    member,
    removeGroupMember,
    toggleUserCardModal,
    getAllGroupMembers,
    keyword,
    canAddMember = false
  } = props;
  const { currentEmployee, groupRosterIdentity } = React.useContext(Context);
  const { t } = useTranslation("chat");

  const setConfirmEdit = useCallback(async () => {
    const members = [employeeToUid(member.info)];
    if (selectedSession.id) {
      const instance = ImService.getInstance();
      const result = await instance.removeGroupMembers(selectedSession.sessionId, members);
      if (result) {
        await removeGroupMember({ member: members[0] });
        // let groupInfo = selectedSession.info as GroupAttribute;
        // getAllGroupMembers?.(groupInfo.gid);
        bus.emit(`chat:session-member-update:${selectedSession?.sessionId}`)
        message.success(t("removed-successfully"));
      } else {
        message.error(t("removal-failed"));
      }
    }
  }, [member]); //eslint-disable-line

  const removeInvitedMember = useCallback(async () => {
    const result = await removeStrangerToGroupRecord({
      groupId: selectedSession?.sessionId,
      nickName: member.info.nickName,
      members: [{ ...employeeToUid(member.info), fromGlobalSearch: "1" }]
    });
    if (result) {
      message.success(t("removed-successfully"));
      bus.emit("chat:invitedList-update");
    } else {
      message.error(t("removal-failed"));
    }
  }, [member.info, selectedSession?.sessionId, t]);

  const modalContent = t(
    `${member?.invitingmember ? "remove-inciting-members-prompt" : "remove-members-prompt"}`,
    {
      memberName: member.info.name
    }
  );

  // 点击移除成员
  const removeMembers = useCallback(() => {
    Prompt({
      title: t("remove-members-reminder"),
      icon: <IconFont type="iconic_failure" style={{ color: "#F96137" }} />,
      content: modalContent,
      cancelText: t("cancelText"),
      onOk: member?.invitingmember ? removeInvitedMember : setConfirmEdit,
      okText: t("determine")
    });
  }, [member]); // eslint-disable-line

  const showDropdownOperate = useMemo(() => {
    return checkShowDropdownOperate({
      selectedSession,
      groupRosterIdentity,
      member,
      currentEmployee
    });
  }, [selectedSession, groupRosterIdentity, member, currentEmployee]);

  const removeMemberPart = useMemo(() => {
    if (!showDropdownOperate) {
      return null;
    }
    return (
      <div
        className="remove-operate-wrapper"
        onClick={removeMembers}
        clstag="pageclick|keycount|Xtbg_Msg_Setting|RemoveMember"
      >
        <IconFont className="remove-icon" type="iconic_pc_im_close" />
      </div>
    );
  }, [showDropdownOperate, removeMembers]);

  const openUserCard = (data: any) => {
    const { info } = data;
    const { userId, teamId, app } = info || {};

    // 如果群成员并且禁止了查看群成员名片
    if (
      !isEqualEmployee(currentEmployee, info) &&
      isGroupOrdinary(groupRosterIdentity) &&
      !isCardViewGroupSession(selectedSession)
    ) {
      message.warn(t("can_not_open_user_card_tip"));
      return false;
    }
    toggleUserCardModal({ visible: true, userId, teamId, appId: app });
  };
  const desc = () => {
    const { user } = member as any;
    const descinfo = getUnitTitleInfo(user.unitName, user.titleName);
    if (keyword?.length) {
      let replaceReg = new RegExp(keyword, "g");
      let replaceString = '<span class="highlight-text">' + keyword + "</span>";
      return descinfo.replace(replaceReg, replaceString);
    } else {
      return descinfo;
    }
  };

  // 再次邀请
  const againInvite = async () => {
    const invitees = [
      {
        ...employeeToUid(member.info),
        name: member.strangerName,
        userType: member.user.userType,
        fromGlobalSearch: "1"
      }
    ];
    const result = await inviteUser(selectedSession.sessionId, invitees, member?.reason, "1");
    if (result.success) {
      message.success(t("invited-sended"));
    } else {
      message.error(result.data?.message || t("add-failed"));
    }
  };

  return (
    <div className="group-member-item">
      <HeadPortrait
        width="32px"
        height="32px"
        fontSize="12px"
        showType={HeadShowType.GROUP_MEMBER}
        imgUrl={member.info.avatar}
        name={member.info.name}
        type={SessionType.SINGLE}
        isLeader={isGroupOwner(member.identity)}
        isAdmin={isGroupAdmin(member.identity)}
        isUnFollow={true}
        member={member}
        showAlias={true}
        session={selectedSession}
        onItemClick={() => {
          openUserCard(member);
        }}
        avatarMask={true}
        desc={desc()}
        keyword={keyword}
        clstag="pageclick|keycount|Xtbg_Msg_Setting|MemberAvatar"
        showExternalTag={true}
        showGroupRoleTag={true}
      />
      {/* 再次邀请和添加群成员的逻辑是一致的，在这个基础上再加个invitingmember===true（当前人员属于待入群) */}
      {canAddMember && member?.invitingmember && (
        <div className="invite_again" onClick={againInvite}>
          再次邀请
        </div>
      )}
      {removeMemberPart}
    </div>
  );
}

function mapStateToProps({ chat }: { chat: ChatState }) {
  return {
    selectedSession: chat.selectedSession
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    async removeGroupMember(data: { member: UID }) {
      await dispatch({ type: "chat/removeGroupMember", payload: data });
    },
    toggleUserCardModal(payload: UserCardPayload) {
      dispatch({ type: "app/toggleUserCardModal", payload });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(GroupMemberOperate);
